import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, LoadingSkeleton, Modal, Select, Textarea } from '@nexora/design-system';
import { apiClient } from '../../../shared/api/client';
import { formatCurrency } from '../../../shared/utils/format';
import { useApproveRenewal, useRejectRenewal, useRenewalPipeline } from './useContractQueries';

/* Central de aprovações.
 * Junta o que já está pendente nas APIs existentes:
 *   GET /orders                         approvalStatus PENDING | PARTIALLY_APPROVED
 *   GET /procurement/purchase-orders    status=PENDING_APPROVAL
 *   GET /procurement/purchase-requests  status=PENDING_APPROVAL
 *   GET /contracts/renewals/pipeline    status PROPOSED
 * Aprovar e reprovar usam as rotas de cada documento. Não há prioridade
 * nem SLA nas APIs, exceto o flag urgent da requisição de compra.
 */

type Kind = 'SALES_ORDER' | 'PURCHASE_ORDER' | 'PURCHASE_REQUEST' | 'RENEWAL';

interface PendingItem {
  key: string;
  kind: Kind;
  id: string;
  number: string;
  typeLabel: string;
  requester: string;
  reason: string;
  amount: number;
  highPriority: boolean;
}

interface OrderRow {
  id: string;
  number: string;
  customerName: string | null;
  approvalStatus: string | null;
  grandTotal: number;
  conditions: string;
}

interface PurchaseOrderRow {
  id: string;
  number: string;
  vendorName: string;
  requestedBy: string;
  total: number;
}

interface PurchaseRequestRow {
  id: string;
  requestNumber: string;
  requestedBy: string;
  justification: string;
  urgent: boolean;
  items: Array<{ estimatedTotalCost: number }>;
}

interface Paginated<T> {
  data: T[];
}

interface PurchaseRequestPage {
  items: PurchaseRequestRow[];
}

const KIND_LABEL: Record<Kind, string> = {
  SALES_ORDER: 'Pedido de venda',
  PURCHASE_ORDER: 'Pedido de compra',
  PURCHASE_REQUEST: 'Requisição de compra',
  RENEWAL: 'Renovação de contrato',
};

function approvalLabel(status: string | null): string {
  if (status === 'PARTIALLY_APPROVED') return 'Aprovação parcial';
  return 'Aguardando aprovação';
}

export function ApprovalCenterPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind | ''>('');
  const [selected, setSelected] = useState<string[]>([]);
  const [rejecting, setRejecting] = useState<PendingItem[]>([]);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState('');

  const orders = useQuery({
    queryKey: ['approvals', 'orders'],
    queryFn: () => apiClient.get<Paginated<OrderRow>>('/orders', { params: { page: '1', limit: '100' } }),
  });
  const purchaseOrders = useQuery({
    queryKey: ['approvals', 'purchase-orders'],
    queryFn: () =>
      apiClient.get<Paginated<PurchaseOrderRow>>('/procurement/purchase-orders', {
        params: { status: 'PENDING_APPROVAL', page: '1', limit: '100' },
      }),
  });
  const purchaseRequests = useQuery({
    queryKey: ['approvals', 'purchase-requests'],
    queryFn: () =>
      apiClient.get<PurchaseRequestPage>('/procurement/purchase-requests', {
        params: { status: 'PENDING_APPROVAL', page: '1', limit: '100' },
      }),
  });
  const renewals = useRenewalPipeline();
  const approveRenewal = useApproveRenewal();
  const rejectRenewal = useRejectRenewal();

  const decide = useMutation({
    mutationFn: async (input: { item: PendingItem; action: 'approve' | 'reject'; reason?: string }) => {
      const { item, action } = input;
      if (item.kind === 'SALES_ORDER') {
        await apiClient.patch(`/orders/${item.id}/${action === 'approve' ? 'approve' : 'reject'}`, action === 'reject' ? { reason: input.reason } : {});
        return;
      }
      if (item.kind === 'PURCHASE_ORDER') {
        await apiClient.post(
          `/procurement/purchase-orders/${item.id}/${action === 'approve' ? 'approve' : 'reject'}`,
          action === 'reject' ? { reason: input.reason } : {},
        );
        return;
      }
      if (item.kind === 'PURCHASE_REQUEST') {
        await apiClient.post(
          `/procurement/purchase-requests/${item.id}/${action === 'approve' ? 'approve' : 'reject'}`,
          action === 'reject' ? { reason: input.reason } : {},
        );
        return;
      }
      if (action === 'approve') {
        await approveRenewal.mutateAsync(item.id);
      } else {
        await rejectRenewal.mutateAsync({ renewalId: item.id, reason: input.reason ?? '' });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'renewals'] });
    },
  });

  const items = useMemo(() => {
    const pending: PendingItem[] = [];
    for (const order of orders.data?.data ?? []) {
      if (order.approvalStatus !== 'PENDING' && order.approvalStatus !== 'PARTIALLY_APPROVED') continue;
      pending.push({
        key: `order:${order.id}`,
        kind: 'SALES_ORDER',
        id: order.id,
        number: order.number || order.id.slice(0, 8),
        typeLabel: KIND_LABEL.SALES_ORDER,
        requester: order.customerName ?? 'Cliente não informado',
        reason: order.conditions?.trim() || approvalLabel(order.approvalStatus),
        amount: order.grandTotal,
        highPriority: false,
      });
    }
    for (const order of purchaseOrders.data?.data ?? []) {
      pending.push({
        key: `po:${order.id}`,
        kind: 'PURCHASE_ORDER',
        id: order.id,
        number: order.number || order.id.slice(0, 8),
        typeLabel: KIND_LABEL.PURCHASE_ORDER,
        requester: order.requestedBy || order.vendorName,
        reason: `Fornecedor: ${order.vendorName || 'não informado'}`,
        amount: order.total,
        highPriority: false,
      });
    }
    for (const request of purchaseRequests.data?.items ?? []) {
      const amount = request.items.reduce((sum, item) => sum + (item.estimatedTotalCost ?? 0), 0);
      pending.push({
        key: `pr:${request.id}`,
        kind: 'PURCHASE_REQUEST',
        id: request.id,
        number: request.requestNumber || request.id.slice(0, 8),
        typeLabel: KIND_LABEL.PURCHASE_REQUEST,
        requester: request.requestedBy,
        reason: request.justification?.trim() || 'Sem justificativa',
        amount,
        highPriority: request.urgent,
      });
    }
    for (const renewal of renewals.data?.renewalPipeline ?? []) {
      if (renewal.status !== 'PROPOSED') continue;
      pending.push({
        key: `renewal:${renewal.id}`,
        kind: 'RENEWAL',
        id: renewal.id,
        number: renewal.id.slice(0, 8),
        typeLabel: KIND_LABEL.RENEWAL,
        requester: renewal.vendorName || renewal.requestedById || 'Não informado',
        reason: renewal.notes?.trim() || renewal.indexName || 'Proposta de renovação',
        amount: renewal.proposedValue,
        highPriority: false,
      });
    }
    return pending;
  }, [orders.data, purchaseOrders.data, purchaseRequests.data, renewals.data]);

  const visible = kind ? items.filter((item) => item.kind === kind) : items;
  const loading = orders.isLoading || purchaseOrders.isLoading || purchaseRequests.isLoading || renewals.isLoading;
  const totalAmount = visible.reduce((sum, item) => sum + item.amount, 0);
  const highCount = visible.filter((item) => item.highPriority).length;
  const errors = [orders.error, purchaseOrders.error, purchaseRequests.error, renewals.error].filter(Boolean);

  const toggle = (key: string): void => {
    setSelected((current) => (current.includes(key) ? current.filter((id) => id !== key) : [...current, key]));
  };

  const selectedItems = visible.filter((item) => selected.includes(item.key));

  const openItem = (item: PendingItem): void => {
    if (item.kind === 'SALES_ORDER') {
      void navigate({ to: '/sales/orders/$id/approval', params: { id: item.id } });
      return;
    }
    if (item.kind === 'PURCHASE_ORDER') {
      void navigate({ to: '/procurement/orders' });
      return;
    }
    if (item.kind === 'PURCHASE_REQUEST') {
      void navigate({ to: '/procurement/requests' });
      return;
    }
    void navigate({ to: '/contracts/renewals' });
  };

  const runApprove = async (targets: PendingItem[]): Promise<void> => {
    setActionError('');
    try {
      for (const item of targets) {
        await decide.mutateAsync({ item, action: 'approve' });
      }
      setSelected([]);
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : 'Não foi possível aprovar.');
    }
  };

  const runReject = async (): Promise<void> => {
    if (!reason.trim()) {
      setActionError('Informe o motivo da reprovação.');
      return;
    }
    setActionError('');
    try {
      for (const item of rejecting) {
        await decide.mutateAsync({ item, action: 'reject', reason: reason.trim() });
      }
      setSelected([]);
      setRejecting([]);
      setReason('');
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : 'Não foi possível reprovar.');
    }
  };

  return (
    <div className="flex flex-col gap-lg p-xl">
      <div className="flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">Central de Aprovações</h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Pedidos, requisições e renovações que ainda aguardam decisão.
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Select aria-label="Tipo de documento" value={kind} onChange={(e) => setKind(e.target.value as Kind | '')}>
            <option value="">Todos os tipos</option>
            {Object.entries(KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          <Button variant="outline" disabled={selectedItems.length === 0} onClick={() => { setReason(''); setRejecting(selectedItems); }}>
            Reprovar
          </Button>
          <Button variant="primary" disabled={selectedItems.length === 0 || decide.isPending} onClick={() => void runApprove(selectedItems)}>
            Aprovar selecionados
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-md md:grid-cols-3">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">Pendentes</p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">{visible.length}</p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">Requisições urgentes</p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">{highCount}</p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">Valor na lista</p>
          <p className="m-0 font-headline-sm text-headline-sm tabular-nums text-on-surface">{formatCurrency(totalAmount)}</p>
        </Card>
      </div>

      {errors.map((error, index) => (
        <Card key={index}>
          <p className="m-0 font-body-sm text-body-sm text-error">{(error as Error).message}</p>
        </Card>
      ))}
      {actionError ? (
        <Card>
          <p className="m-0 font-body-sm text-body-sm text-error">{actionError}</p>
        </Card>
      ) : null}

      {loading ? (
        <Card>
          <LoadingSkeleton count={4} height="40px" />
        </Card>
      ) : null}

      {!loading && visible.length === 0 ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">Nenhum documento pendente de aprovação.</p>
        </Card>
      ) : null}

      {!loading && visible.length > 0 ? (
        <Card padding="0">
          <div className="grid grid-cols-12 gap-sm border-b border-outline-variant px-md py-sm font-label-sm text-label-sm uppercase text-on-surface-variant">
            <div className="col-span-1" />
            <div className="col-span-3">Documento</div>
            <div className="col-span-2">Solicitante</div>
            <div className="col-span-3">Motivo</div>
            <div className="col-span-1 text-right">Valor</div>
            <div className="col-span-2 text-right">Ação</div>
          </div>
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {visible.map((item) => (
              <li key={item.key} className="grid grid-cols-12 items-center gap-sm border-b border-outline-variant px-md py-sm last:border-b-0">
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    aria-label={`Selecionar ${item.number}`}
                    checked={selected.includes(item.key)}
                    onChange={() => toggle(item.key)}
                  />
                </div>
                <button type="button" className="col-span-3 bg-transparent p-0 text-left" onClick={() => openItem(item)}>
                  <span className="block font-label-md text-label-md font-semibold text-on-surface">{item.number}</span>
                  <span className="block font-body-sm text-body-sm text-on-surface-variant">{item.typeLabel}</span>
                </button>
                <div className="col-span-2 font-body-sm text-body-sm text-on-surface">{item.requester}</div>
                <div className="col-span-3">
                  {item.highPriority ? <Badge variant="error">Urgente</Badge> : null}
                  <span className="font-body-sm text-body-sm text-on-surface-variant">{item.reason}</span>
                </div>
                <div className="col-span-1 text-right font-label-md text-label-md text-on-surface">{formatCurrency(item.amount)}</div>
                <div className="col-span-2 flex justify-end gap-xs">
                  <Button size="sm" variant="outline" onClick={() => { setReason(''); setRejecting([item]); }}>Reprovar</Button>
                  <Button size="sm" variant="primary" disabled={decide.isPending} onClick={() => void runApprove([item])}>Aprovar</Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Modal
        isOpen={rejecting.length > 0}
        onClose={() => setRejecting([])}
        title="Reprovar"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejecting([])}>Cancelar</Button>
            <Button variant="danger" loading={decide.isPending} onClick={() => void runReject()}>Reprovar</Button>
          </>
        }
      >
        <Textarea label="Motivo *" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </div>
  );
}
